/* eslint-disable import/no-cycle */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { FieldWrapper, Filter, Input, Select } from 'components';
import { DDOption, DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { getQueryStringValue, useDebounce } from 'hooks';
import { getJobTitlesRequest, getOrgJobTitlesRequest, selectJobTitlesBySite } from 'store/Collections';
import { IEmployerState } from 'store/types';
import { ApiQueryOperator, EmployeeRole, EmployeeStatus, EmployeeType, IApiQueryParam, SiteRole } from 'types';
import { formatJobTitle } from 'utils/jobs';

type InnerRoles = 'orgadmin' | 'siteadmin' | 'sitemanager' | 'employee';

const typeToSiteRoleMapping = {
  orgadmin: { type: 'admin' },
  siteadmin: { type: 'user', siteRole: 'admin' },
  sitemanager: { type: 'user', siteRole: 'manager' },
  employee: { type: 'user', siteRole: 'employee' },
} as {
  [K in InnerRoles]: { type: EmployeeType; siteRole: SiteRole };
};

const compositeRoleMapping = {
  admin: 'orgadmin',
  useradmin: 'siteadmin',
  usermanager: 'sitemanager',
  useremployee: 'employee',
} as {
  [key: string]: string;
};

const StyledFieldWrapper = styled(FieldWrapper)`
  margin-bottom: 0;
`;

interface IUsersFiltersProps {
  isVisible: boolean;
  roleVisible: boolean;
  jobTitleVisible: boolean;
  statusVisible: boolean;
  statusOptions?: DDOption[];
  userTypeOptions?: DDOption[];
  className?: string;
  nested?: boolean;
  siteId?: string;
  organizationId?: string;
  organization?: IEmployerState;
  urlQuery: IApiQueryOptions;
  onFiltersChange: (queryList: IApiQueryParam[]) => void;
}

interface IUserFiltersFormik {
  name: string;
  type: Array<string | null>;
  sitesRole: Array<string | null>;
  jobTitle: Array<{ title: string } | null>;
  status: any[];
  compositeRole?: string;
}

const UsersFilters: React.FC<IUsersFiltersProps> = ({ isVisible, roleVisible, statusVisible, statusOptions, jobTitleVisible, userTypeOptions, className, nested, siteId, organizationId, urlQuery, onFiltersChange, ...rest }) => {
  const dispatch = useDispatch();
  const jobTitles = useSelector(selectJobTitlesBySite(siteId || organizationId));

  const nameSearch = getQueryStringValue(urlQuery.q, 'name', ApiQueryOperator.like) as string;
  const typeFilter = getQueryStringValue(urlQuery.q, 'type', ApiQueryOperator.in) as string;
  const sitesRoleFilter = getQueryStringValue(urlQuery.q, 'sites.role', ApiQueryOperator.in) as string;
  const jobTitleFilter = getQueryStringValue(urlQuery.q, 'sites.job.title', ApiQueryOperator.like)?.slice(1, -1) as string;
  const statusFilter = getQueryStringValue(urlQuery.q, 'status', ApiQueryOperator.in);
  const actionRequiredFilter = getQueryStringValue(urlQuery.q, 'action_required', ApiQueryOperator.in);

  const currentStatusFilters = actionRequiredFilter
    ? [
        {
          key: 'status',
          operator: ApiQueryOperator.in,
          value: statusFilter,
        },
        {
          key: 'action_required',
          operator: ApiQueryOperator.in,
          value: actionRequiredFilter,
        },
      ]
    : [
        {
          key: 'status',
          operator: ApiQueryOperator.nin,
          value: [EmployeeStatus.APPROVED, EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED, EmployeeStatus.DISMISSED],
        },
      ];

  const { values, handleSubmit, getFieldProps, setFieldValue } = useFormik({
    initialValues: {
      name: nameSearch || '',
      compositeRole: '',
      type: typeFilter ? [typeFilter] : [],
      sitesRole: sitesRoleFilter ? [sitesRoleFilter] : [],
      jobTitle: jobTitleFilter ? [{ title: decodeURIComponent(jobTitleFilter) }] : typeFilter === EmployeeType.ADMIN && !roleVisible ? [{ title: EmployeeRole.ADMIN }] : [],
      status: currentStatusFilters,
    },
    onSubmit: (values: IUserFiltersFormik) => {
      const nameFilter = isEmpty(values.name.trim())
        ? []
        : [
            {
              key: 'name',
              operator: ApiQueryOperator.like,
              value: values.name.trim(),
            },
          ];
      const typeFilter =
        isNil(values.type[0]) || !roleVisible
          ? []
          : [
              {
                key: 'type',
                operator: ApiQueryOperator.in,
                value: values.type[0],
              },
            ];
      const sitesRoleFilter =
        isNil(values.sitesRole[0]) || !roleVisible
          ? []
          : [
              {
                key: 'sites.role',
                operator: ApiQueryOperator.in,
                value: values.sitesRole[0],
              },
            ];

      const statusFilter = values.status;

      let jobFilter: IApiQueryParam[] = [];
      // This exception is for adding 'admin' as an option in the jobTitle filter
      if (values.jobTitle[0]?.title === EmployeeType.ADMIN && jobTitleVisible) {
        jobFilter = [
          {
            key: 'type',
            operator: ApiQueryOperator.in,
            value: EmployeeType.ADMIN,
          },
        ];
      } else if (!isNil(values.jobTitle[0]) && jobTitleVisible) {
        jobFilter = [
          ...(values.jobTitle[0]?.title
            ? [
                {
                  key: 'sites.job.title',
                  operator: ApiQueryOperator.like,
                  value: `^${values.jobTitle[0].title}$`,
                },
              ]
            : []),
        ];
      }

      const queryList = [...nameFilter, ...typeFilter, ...sitesRoleFilter, ...(statusFilter || currentStatusFilters), ...jobFilter];

      onFiltersChange(queryList);
    },
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const delayedHandleChange = useDebounce(handleSubmit);

  const roleOptions = userTypeOptions || [
    ...[{ value: null, label: 'All' }],
    ...[
      { value: 'orgadmin', label: 'Org Admin' },
      { value: 'siteadmin', label: 'Site Admin' },
      { value: 'sitemanager', label: 'Manager' },
      { value: 'employee', label: 'Employee' },
    ],
  ];

  useEffect(() => {
    if (isVisible && !jobTitles?.length) {
      if (siteId) {
        dispatch(getJobTitlesRequest({ siteId }));
      } else if (organizationId) {
        dispatch(getOrgJobTitlesRequest({ organizationId, includeInactive: false }));
      }
    }
  }, [siteId, organizationId, isVisible]);

  const jobTitleOptions = jobTitles?.map((jobTitle) => ({
    label: formatJobTitle(jobTitle.title),
    value: { title: jobTitle.title },
  }));

  const jobOptions = [
    { value: null, label: 'All' },
    // Only add the 'Org Admin' option if role filter is not visible
    ...(!roleVisible
      ? [
          {
            value: { title: EmployeeRole.ADMIN },
            label: formatJobTitle(DEFAULT_ORG_ADMIN_JOB_TITLE),
          },
        ]
      : []),
    ...(jobTitleOptions || []),
  ];

  return (
    <Filter onSubmit={handleSubmit} isVisible={isVisible} nested={nested} className={className} {...rest}>
      <StyledFieldWrapper label="Name" data-cy="name-filter">
        <Input id="name" type="text" placeholder="Name" onKeyDown={delayedHandleChange} {...getFieldProps('name')} />
      </StyledFieldWrapper>

      {roleVisible && (
        <StyledFieldWrapper label="Role">
          <Select
            {...getFieldProps('compositeRole')}
            placeholder="All"
            multi={false}
            options={roleOptions}
            values={roleOptions.filter(
              (ro) =>
                ro.value ===
                compositeRoleMapping[
                  `${typeFilter}${sitesRoleFilter || ''}` // converts to admin | useradmin | usermanager | useremployee
                ]
            )}
            onChange={(values) => {
              if (values?.[0]?.value === null) {
                setFieldValue('type', []);
                setFieldValue('sitesRole', []);
                setFieldValue('compositeRole', '');
              } else {
                const types: EmployeeType[] = [];
                const sitesRoles: SiteRole[] = [];
                values
                  .map((val) => val.value)
                  .forEach((key: InnerRoles) => {
                    if (!typeToSiteRoleMapping[key]) return;
                    if (typeToSiteRoleMapping[key].type) {
                      types.push(typeToSiteRoleMapping[key].type);
                    }
                    if (typeToSiteRoleMapping[key].siteRole !== undefined) {
                      sitesRoles.push(typeToSiteRoleMapping[key].siteRole);
                    }
                    setFieldValue('type', types);
                    setFieldValue('sitesRole', sitesRoles);
                    setFieldValue('compositeRole', `${types[0]}${sitesRoles[0]}`);
                  });
              }
              handleSubmit();
            }}
            data-cy="role-filter"
          />
        </StyledFieldWrapper>
      )}
      {jobTitleVisible && (
        <StyledFieldWrapper label="Job Title">
          <Select
            {...getFieldProps('jobTitle')}
            placeholder="All"
            multi={false}
            options={jobOptions}
            values={jobOptions.filter((jo) => isEqual(jo.value, values.jobTitle?.[0]))}
            onChange={(values) => {
              setFieldValue('jobTitle', values?.[0]?.value === null ? [] : values.map((val) => val.value));
              handleSubmit();
            }}
            data-cy="job-filter"
          />
        </StyledFieldWrapper>
      )}

      {statusVisible && statusOptions && (
        <StyledFieldWrapper label="Status">
          <Select
            {...getFieldProps('status')}
            placeholder="All"
            multi={false}
            options={statusOptions}
            values={statusOptions.filter((so) => isEqual(values.status, so.value))}
            onChange={(values) => {
              setFieldValue('status', values?.[0]?.value);
              handleSubmit();
            }}
            data-cy="status-filter"
          />
        </StyledFieldWrapper>
      )}
    </Filter>
  );
};

export default UsersFilters;
